<script setup lang="ts">
import { Skeleton, ZhSystemLabel, ZhTooltip } from '@zerohuman/ui'
import { type AgentUsage, type AgentUsageBalance, formatUsdCents } from '@/api'
import { formatDaysRemaining, formatResetsIn, usageBarClass, usageDayClass } from '@/lib/agents'

defineProps<{
    usage: AgentUsage | null
    loading?: boolean
}>()

function barWidth(percent: number): string {
    return `${Math.min(100, Math.max(0, percent))}%`
}

/**
 * The key's last four characters, or null for no key row (#1773). Read from `keyLastFour` only, never from a
 * provider `label` an older API may still send, and clamped to four characters whatever the field holds.
 */
function keyTail(balance: AgentUsageBalance): string | null {
    const tail = balance.keyLastFour?.trim().slice(-4) ?? ''
    return tail.length === 4 ? tail : null
}

/** A day's bar against the busiest day shown: never under 12% when something was spent, a 3px stub when nothing was. */
function dayHeight(usdCents: number, days: AgentUsageBalance['days']): string {
    const busiest = Math.max(...days.map(item => item.usdCents), 1)
    return usdCents > 0 ? `${Math.max(12, (usdCents / busiest) * 100)}%` : '3px'
}
</script>

<!--
    Every bar is an <svg> box sized by its own width or height attribute: an svg's size is a presentation attribute,
    so a data-driven bar needs no inline style. Its colour comes from lib/agents (usageBarClass, usageDayClass), and the
    meter track is the row's kit panel itself, the same ground the muted track used to draw on it.
-->
<template>
    <div v-if="loading" data-testid="agent-usage" class="space-y-2" aria-busy="true">
        <ZhSystemLabel tone="muted">Usage</ZhSystemLabel>
        <div class="space-y-2">
            <Skeleton v-for="n in 2" :key="n" class="h-1.5" />
        </div>
    </div>
    <div
        v-else-if="usage && (usage.quotas.length || usage.balance || usage.message)"
        data-testid="agent-usage"
        class="space-y-2.5"
    >
        <ZhSystemLabel tone="muted">Usage</ZhSystemLabel>
        <ul v-if="usage.kind === 'subscription' && usage.quotas.length" class="space-y-2.5">
            <li v-for="quota in usage.quotas" :key="quota.id" class="space-y-1">
                <div class="flex items-baseline justify-between gap-3 text-xs">
                    <span>{{ quota.label }}</span>
                    <span class="tabular-nums">{{ Math.round(quota.usedPercent) }}%</span>
                </div>
                <p v-if="formatResetsIn(quota.resetsAt)" class="text-muted-foreground text-xs">
                    {{ formatResetsIn(quota.resetsAt) }}
                </p>
                <div
                    class="h-1.5 overflow-hidden"
                    role="meter"
                    :aria-label="quota.label"
                    :aria-valuenow="Math.round(quota.usedPercent)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    <svg
                        :width="barWidth(quota.usedPercent)"
                        aria-hidden="true"
                        class="block h-full transition-all"
                        :class="usageBarClass(quota.usedPercent)"
                    />
                </div>
            </li>
        </ul>
        <div v-else-if="usage.kind === 'balance' && usage.balance" class="space-y-2.5">
            <div class="space-y-1">
                <div class="flex items-baseline justify-between gap-3 text-xs">
                    <span>Balance</span>
                    <span v-if="usage.balance.remainingCents !== null" class="tabular-nums">
                        {{ formatUsdCents(usage.balance.remainingCents) }} left
                    </span>
                    <span v-else-if="usage.balance.usedPercent !== null" class="tabular-nums">
                        {{ Math.round(usage.balance.usedPercent) }}%
                    </span>
                </div>
                <p
                    v-if="keyTail(usage.balance)"
                    data-testid="agent-usage-key"
                    class="text-muted-foreground truncate font-mono text-xs"
                >
                    Key · ••••{{ keyTail(usage.balance) }}
                </p>
                <div
                    v-if="usage.balance.usedPercent !== null"
                    class="h-1.5 overflow-hidden"
                    role="meter"
                    aria-label="Balance used"
                    :aria-valuenow="Math.round(usage.balance.usedPercent)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    <svg
                        :width="barWidth(usage.balance.usedPercent)"
                        aria-hidden="true"
                        class="block h-full"
                        :class="usageBarClass(usage.balance.usedPercent)"
                    />
                </div>
            </div>
            <p class="text-muted-foreground text-xs">
                <template v-if="usage.balance.todayCents !== null">
                    Today {{ formatUsdCents(usage.balance.todayCents) }}
                </template>
                <template v-if="usage.balance.weekCents !== null">
                    <template v-if="usage.balance.todayCents !== null"> · </template>
                    {{ formatUsdCents(usage.balance.weekCents) }} this week
                </template>
                <template v-if="usage.balance.monthCents !== null && usage.balance.weekCents === null">
                    {{ formatUsdCents(usage.balance.monthCents) }} this month
                </template>
            </p>
            <p v-if="formatDaysRemaining(usage.balance)" class="text-xs">
                {{ formatDaysRemaining(usage.balance) }}
            </p>
            <div
                v-if="usage.balance.days.length"
                class="flex h-8 items-end gap-0.5"
                aria-label="Usage by day"
            >
                <ZhTooltip
                    v-for="day in usage.balance.days"
                    :key="day.date"
                    :text="`${day.date}: ${formatUsdCents(day.usdCents)}`"
                >
                    <div class="flex h-full min-w-0 flex-1 items-end">
                        <svg
                            :height="dayHeight(day.usdCents, usage.balance.days)"
                            aria-hidden="true"
                            class="block min-h-0.5 w-full"
                            :class="usageDayClass(day.usdCents)"
                        />
                    </div>
                </ZhTooltip>
            </div>
        </div>
        <p v-else-if="usage.message" class="text-muted-foreground text-xs">{{ usage.message }}</p>
    </div>
</template>
